import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { isSupported as isAvModerationSupported } from '../../../av-moderation/functions';
import Avatar from '../../../base/avatar/components/Avatar';
import { isIosMobileBrowser, isMobileBrowser } from '../../../base/environment/utils';
import { MEDIA_TYPE } from '../../../base/media/constants';
import { PARTICIPANT_ROLE } from '../../../base/participants/constants';
import { getLocalParticipant, hasRaisedHand } from '../../../base/participants/functions';
import { isParticipantAudioMuted, isParticipantVideoMuted } from '../../../base/tracks/functions.any';
import ContextMenu from '../../../base/ui/components/web/ContextMenu';
import ContextMenuItemGroup from '../../../base/ui/components/web/ContextMenuItemGroup';
import { getBreakoutRooms, getCurrentRoomId, isInBreakoutRoom } from '../../../breakout-rooms/functions';
import { displayVerification } from '../../../e2ee/functions';
import { setVolume } from '../../../filmstrip/actions.web';
import { isStageFilmstripAvailable } from '../../../filmstrip/functions.web';
import { QUICK_ACTION_BUTTON } from '../../../participants-pane/constants';
import { getQuickActionButtonType, isForceMuted } from '../../../participants-pane/functions';
import { requestRemoteControl, stopController } from '../../../remote-control/actions';
import { getParticipantMenuButtonsWithNotifyClick, showOverflowDrawer } from '../../../toolbox/functions.web';
import { NOTIFY_CLICK_MODE } from '../../../toolbox/types';
import { iAmVisitor } from '../../../visitors/functions';
import { PARTICIPANT_MENU_BUTTONS as BUTTONS } from '../../constants';
import AskToUnmuteButton from './AskToUnmuteButton';
import ConnectionStatusButton from './ConnectionStatusButton';
import CustomOptionButton from './CustomOptionButton';
import DemoteToVisitorButton from './DemoteToVisitorButton';
import GrantModeratorButton from './GrantModeratorButton';
import KickButton from './KickButton';
import LowerHandButton from './LowerHandButton';
import MuteButton from './MuteButton';
import MuteEveryoneElseButton from './MuteEveryoneElseButton';
import MuteEveryoneElsesVideoButton from './MuteEveryoneElsesVideoButton';
import MuteVideoButton from './MuteVideoButton';
import PrivateMessageMenuButton from './PrivateMessageMenuButton';
import RemoteControlButton, { REMOTE_CONTROL_MENU_STATES } from './RemoteControlButton';
import SendToRoomButton from './SendToRoomButton';
import TogglePinToStageButton from './TogglePinToStageButton';
import VerifyParticipantButton from './VerifyParticipantButton';
const useStyles = makeStyles()(theme => {
    return {
        text: {
            color: theme.palette.text02,
            padding: '10px 16px',
            height: '40px',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            boxSizing: 'border-box'
        }
    };
});
const ParticipantContextMenu = ({ className, closeDrawer, drawerParticipant, offsetTarget, onEnter, onLeave, onSelect, participant, remoteControlState, thumbnailMenu }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { classes: styles } = useStyles();
    const localParticipant = useSelector(getLocalParticipant);
    const _isModerator = Boolean(localParticipant?.role === PARTICIPANT_ROLE.MODERATOR);
    const _isVideoForceMuted = useSelector(state => isForceMuted(participant, MEDIA_TYPE.VIDEO, state));
    const _isAudioMuted = useSelector((state) => isParticipantAudioMuted(participant, state));
    const _isVideoMuted = useSelector((state) => isParticipantVideoMuted(participant, state));
    const _overflowDrawer = useSelector(showOverflowDrawer);
    const { remoteVideoMenu = {}, disableRemoteMute, startSilent, customParticipantMenuButtons } = useSelector((state) => state['features/base/config']);
    const visitorsMode = useSelector((state) => iAmVisitor(state));
    const visitorsSupported = useSelector((state) => state['features/visitors'].supported);
    const { disableDemote, disableKick, disableGrantModerator, disablePrivateChat } = remoteVideoMenu;
    const { participantsVolume } = useSelector((state) => state['features/filmstrip']);
    const _volume = (participant?.local ?? true ? undefined
        : participant?.id ? participantsVolume[participant?.id] : undefined) ?? 1;
    const isBreakoutRoom = useSelector(isInBreakoutRoom);
    const isModerationSupported = useSelector((state) => isAvModerationSupported()(state));
    const raisedHands = hasRaisedHand(participant);
    const stageFilmstrip = useSelector(isStageFilmstripAvailable);
    const shouldDisplayVerification = useSelector((state) => displayVerification(state, participant?.id));
    const buttonsWithNotifyClick = useSelector(getParticipantMenuButtonsWithNotifyClick);
    const _currentRoomId = useSelector(getCurrentRoomId);
    const _rooms = Object.values(useSelector(getBreakoutRooms));
    const _onVolumeChange = useCallback(value => {
        dispatch(setVolume(participant.id, value));
    }, [setVolume, dispatch]);
    const _getCurrentParticipantId = useCallback(() => {
        const drawer = _overflowDrawer && !thumbnailMenu;
        return (drawer ? drawerParticipant?.participantID : participant?.id) ?? '';
    }, [thumbnailMenu, _overflowDrawer, drawerParticipant, participant]);
    const notifyClick = useCallback((buttonKey) => {
        const notifyMode = buttonsWithNotifyClick?.get(buttonKey);
        if (!notifyMode) {
            return;
        }
        APP.API.notifyParticipantMenuButtonClicked(buttonKey, _getCurrentParticipantId(), notifyMode === NOTIFY_CLICK_MODE.PREVENT_AND_NOTIFY);
    }, [buttonsWithNotifyClick, _getCurrentParticipantId]);
    const onBreakoutRoomButtonClick = useCallback(() => {
        onSelect(true);
    }, [onSelect]);
    const isClickedFromParticipantPane = useMemo(() => !_overflowDrawer && !thumbnailMenu, [_overflowDrawer, thumbnailMenu]);
    const quickActionButtonType = useSelector((state) => getQuickActionButtonType(participant, _isAudioMuted, _isVideoMuted, state));
    const buttons = [];
    const buttons2 = [];
    const showVolumeSlider = !startSilent
        && !isIosMobileBrowser()
        && (_overflowDrawer || thumbnailMenu)
        && typeof _volume === 'number'
        && !isNaN(_volume);
    const getButtonProps = useCallback((key) => {
        const notifyMode = buttonsWithNotifyClick?.get(key);
        const shouldNotifyClick = typeof notifyMode !== 'undefined';
        return {
            key,
            notifyMode,
            notifyClick: shouldNotifyClick ? () => notifyClick(key) : undefined,
            participantID: _getCurrentParticipantId()
        };
    }, [_getCurrentParticipantId, buttonsWithNotifyClick, notifyClick]);
    if (_isModerator) {
        if (isModerationSupported) {
            if (_isAudioMuted && !participant.isSilent
                && !(isClickedFromParticipantPane && quickActionButtonType === QUICK_ACTION_BUTTON.ASK_TO_UNMUTE)) {
                buttons.push(React.createElement(AskToUnmuteButton, { ...getButtonProps(BUTTONS.ASK_UNMUTE), buttonType: MEDIA_TYPE.AUDIO }));
            }
            if (_isVideoForceMuted
                && !(isClickedFromParticipantPane && quickActionButtonType === QUICK_ACTION_BUTTON.ALLOW_VIDEO)) {
                buttons.push(React.createElement(AskToUnmuteButton, { ...getButtonProps(BUTTONS.ALLOW_VIDEO), buttonType: MEDIA_TYPE.VIDEO }));
            }
        }
        if (!disableRemoteMute && !participant.isSilent) {
            if (!(isClickedFromParticipantPane && quickActionButtonType === QUICK_ACTION_BUTTON.MUTE)) {
                buttons.push(React.createElement(MuteButton, { ...getButtonProps(BUTTONS.MUTE) }));
            }
            buttons.push(React.createElement(MuteEveryoneElseButton, { ...getButtonProps(BUTTONS.MUTE_OTHERS) }));
            if (!(isClickedFromParticipantPane && quickActionButtonType === QUICK_ACTION_BUTTON.STOP_VIDEO)) {
                buttons.push(React.createElement(MuteVideoButton, { ...getButtonProps(BUTTONS.MUTE_VIDEO) }));
            }
            buttons.push(React.createElement(MuteEveryoneElsesVideoButton, { ...getButtonProps(BUTTONS.MUTE_OTHERS_VIDEO) }));
        }
        if (raisedHands) {
            buttons2.push(React.createElement(LowerHandButton, { ...getButtonProps(BUTTONS.LOWER_PARTICIPANT_HAND) }));
        }
        if (!disableGrantModerator && !isBreakoutRoom) {
            buttons2.push(React.createElement(GrantModeratorButton, { ...getButtonProps(BUTTONS.GRANT_MODERATOR) }));
        }
        if (!disableDemote && visitorsSupported && _isModerator) {
            buttons2.push(React.createElement(DemoteToVisitorButton, { ...getButtonProps(BUTTONS.DEMOTE) }));
        }
        if (!disableKick) {
            buttons2.push(React.createElement(KickButton, { ...getButtonProps(BUTTONS.KICK) }));
        }
        if (shouldDisplayVerification) {
            buttons2.push(React.createElement(VerifyParticipantButton, { ...getButtonProps(BUTTONS.VERIFY) }));
        }
    }
    if (stageFilmstrip) {
        buttons2.push(React.createElement(TogglePinToStageButton, { ...getButtonProps(BUTTONS.PIN_TO_STAGE) }));
    }
    if (!disablePrivateChat && !visitorsMode) {
        buttons2.push(React.createElement(PrivateMessageMenuButton, { ...getButtonProps(BUTTONS.PRIVATE_MESSAGE) }));
    }
    if (thumbnailMenu && isMobileBrowser()) {
        buttons2.push(React.createElement(ConnectionStatusButton, { ...getButtonProps(BUTTONS.CONN_STATUS) }));
    }
    if (thumbnailMenu && remoteControlState) {
        const onRemoteControlToggle = useCallback(() => {
            if (remoteControlState === REMOTE_CONTROL_MENU_STATES.STARTED) {
                dispatch(stopController(true));
            }
            else if (remoteControlState === REMOTE_CONTROL_MENU_STATES.NOT_STARTED) {
                dispatch(requestRemoteControl(_getCurrentParticipantId()));
            }
        }, [dispatch, remoteControlState, stopController, requestRemoteControl]);
        buttons2.push(React.createElement(RemoteControlButton, { ...getButtonProps(BUTTONS.REMOTE_CONTROL), onClick: onRemoteControlToggle, remoteControlState: remoteControlState }));
    }
    if (customParticipantMenuButtons) {
        customParticipantMenuButtons.forEach(({ icon, id, text }) => {
            buttons2.push(React.createElement(CustomOptionButton, { icon: icon, key: id, 
                // eslint-disable-next-line react/jsx-no-bind
                onClick: () => notifyClick(id), text: text }));
        });
    }
    const breakoutRoomsButtons = [];
    if (!thumbnailMenu && _isModerator) {
        _rooms.forEach(room => {
            if (room.id !== _currentRoomId) {
                breakoutRoomsButtons.push(React.createElement(SendToRoomButton, { ...getButtonProps(BUTTONS.SEND_PARTICIPANT_TO_ROOM), key: room.id, onClick: onBreakoutRoomButtonClick, room: room }));
            }
        });
    }
    return (React.createElement(ContextMenu, { className: className, entity: participant, hidden: thumbnailMenu ? false : undefined, inDrawer: thumbnailMenu && _overflowDrawer, isDrawerOpen: Boolean(drawerParticipant), offsetTarget: offsetTarget, onClick: onSelect, onDrawerClose: thumbnailMenu ? onSelect : closeDrawer, onMouseEnter: onEnter, onMouseLeave: onLeave },
        !thumbnailMenu && _overflowDrawer && drawerParticipant && React.createElement(ContextMenuItemGroup, { actions: [{
                    accessibilityLabel: drawerParticipant.displayName,
                    customIcon: React.createElement(Avatar, { participantId: drawerParticipant.participantID, size: 20 }),
                    text: drawerParticipant.displayName
                }] }),
        buttons.length > 0 && (React.createElement(ContextMenuItemGroup, null, buttons)),
        React.createElement(ContextMenuItemGroup, null, buttons2),
        breakoutRoomsButtons.length > 0 && (React.createElement(ContextMenuItemGroup, null,
            React.createElement("div", { className: styles.text }, t('breakoutRooms.actions.sendToBreakoutRoom')),
            breakoutRoomsButtons))));
};
export default ParticipantContextMenu;
